// Browser-side WebRTC client for the Kim voice agent.
//
// Standard offer/answer SDP exchange against the apbe proxy
// (/api/naytive/webrtc/offer). Trickle ICE candidates go through
// PATCH on the same path. This file is framework-agnostic — the
// React modal just instantiates it and listens to the callbacks.

import { naytiveAPI } from './api';

export class VoiceAgentClient {
  constructor({ sessionId, onConnectionState, onRemoteStream, onLocalStream, onError }) {
    if (!sessionId) throw new Error('sessionId required');
    this.sessionId = sessionId;
    this.onConnectionState = onConnectionState || (() => {});
    this.onRemoteStream = onRemoteStream || (() => {});
    this.onLocalStream = onLocalStream || (() => {});
    this.onError = onError || console.error;

    this.pc = null;
    this.pcId = null;          // assigned by the server in the answer
    this.localStream = null;
    this._iceQueue = [];        // candidates discovered before we get pcId back
    this._iceFlushTimer = null;
  }

  async start() {
    // 1. Mic capture
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
    this.onLocalStream(this.localStream);

    // 2. PeerConnection — Google's STUN is fine for local dev
    this.pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    // Pipe local mic into PC
    for (const track of this.localStream.getAudioTracks()) {
      this.pc.addTrack(track, this.localStream);
    }

    // Surface state changes to the React modal
    this.pc.oniceconnectionstatechange = () => {
      this.onConnectionState(this.pc.iceConnectionState);
    };
    this.pc.onconnectionstatechange = () => {
      this.onConnectionState(this.pc.connectionState);
    };

    // Remote audio — handed to the React modal which attaches it to
    // an <audio> element for playback.
    this.pc.ontrack = (event) => {
      if (event.track.kind === 'audio') {
        const stream = event.streams[0] || new MediaStream([event.track]);
        this.onRemoteStream(stream);
      }
    };

    // Trickle ICE — Kim's side trickles too; we forward each local
    // candidate to the server as it's discovered.
    this.pc.onicecandidate = ({ candidate }) => {
      if (!candidate) return;
      this._iceQueue.push({
        candidate: candidate.candidate,
        sdp_mid: candidate.sdpMid,
        sdp_mline_index: candidate.sdpMLineIndex,
      });
      this._scheduleIceFlush();
    };

    // 3. Offer
    const offer = await this.pc.createOffer({ offerToReceiveAudio: true });
    await this.pc.setLocalDescription(offer);

    // 4. POST offer to apbe → voice-agent
    const { data } = await naytiveAPI.sendOffer({
      sdp: this.pc.localDescription.sdp,
      type: this.pc.localDescription.type,
      request_data: { session_id: this.sessionId },
    });

    if (!data?.sdp || !data?.type) {
      throw new Error('voice-agent returned no SDP answer');
    }
    this.pcId = data.pc_id || null;

    // 5. Answer
    await this.pc.setRemoteDescription({ sdp: data.sdp, type: data.type });

    // Flush any ICE candidates queued before we had a pcId
    this._scheduleIceFlush();
  }

  _scheduleIceFlush() {
    // Coalesce candidates to one PATCH per ~100ms.
    if (!this.pcId) return;
    if (this._iceFlushTimer) return;
    this._iceFlushTimer = setTimeout(() => this._flushIce(), 100);
  }

  async _flushIce() {
    this._iceFlushTimer = null;
    const candidates = this._iceQueue.splice(0);
    if (!candidates.length || !this.pcId) return;
    try {
      await naytiveAPI.sendIceCandidates({
        pc_id: this.pcId,
        candidates,
      });
    } catch (err) {
      this.onError(`ICE patch failed: ${err.message}`);
    }
  }

  stop() {
    try {
      if (this._iceFlushTimer) clearTimeout(this._iceFlushTimer);
      if (this.pc) {
        this.pc.getSenders().forEach((s) => { try { s.track?.stop(); } catch {} });
        this.pc.close();
      }
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
      }
    } catch (err) {
      this.onError(`stop error: ${err.message}`);
    }
    this.pc = null;
    this.localStream = null;
  }
}
